import { queryOptions, useQuery } from "@tanstack/react-query";
import {
  mvpApiGetMyMetrics,
  mvpApiGetUserMetrics,
  mvpApiListViewableUsers,
} from "@labeling-tools/api/user-insights/generated/sdk.gen";
import type {
  InsightsUserBriefSchema,
  UserMetricsSchema,
} from "@labeling-tools/api/user-insights/generated/types.gen";
import {
  COVERAGE_BETA_TASK_TYPE,
  OOS_CONFIRMATION_TASK_TYPE,
  pickDefaultTaskType,
} from "@/utils/profile-metrics";

export type { InsightsUserBriefSchema, UserMetricsSchema };

export type MetricsPeriod = "all_time" | "last_30d" | "last_7d";

/**
 * Metrics for one user, task type and period. `userId` null means the signed-in
 * user (/users/me/metrics, which also carries the viewer's `role`). A null
 * `taskType` leaves the query disabled until the caller knows which type to show.
 */
function metricsQueryOptions(
  userId: string | null,
  taskType: string | null,
  period: MetricsPeriod,
) {
  return queryOptions<UserMetricsSchema>({
    queryKey:
      userId === null
        ? ["user-insights", "me", taskType, period]
        : ["user-insights", "user", userId, taskType, period],
    queryFn: async () => {
      const query = { task_type: taskType!, period };
      const res =
        userId === null
          ? await mvpApiGetMyMetrics({ query })
          : await mvpApiGetUserMetrics({ path: { user_id: userId }, query });
      if (res.error) throw new Error(`Failed to load metrics: ${res.error}`);
      return res.data as UserMetricsSchema;
    },
    enabled: taskType !== null,
    staleTime: 5 * 60 * 1000,
  });
}

export function useMyUserMetrics(
  taskType: string | null,
  period: MetricsPeriod = "all_time",
) {
  return useQuery(metricsQueryOptions(null, taskType, period));
}

export function useUserMetrics(
  userId: string,
  taskType: string | null,
  period: MetricsPeriod = "all_time",
) {
  return useQuery(metricsQueryOptions(userId, taskType, period));
}

/**
 * The task type a profile should open on for `userId` (null = the signed-in
 * user): whichever of OOS Confirmation and Coverage Beta they spent more time
 * on over the last 30 days. `taskType` is null until both have loaded, so the
 * page can wait instead of rendering OOS and then swapping.
 */
export function useDefaultTaskType(userId: string | null) {
  const oos = useQuery(
    metricsQueryOptions(userId, OOS_CONFIRMATION_TASK_TYPE, "last_30d"),
  );
  const beta = useQuery(
    metricsQueryOptions(userId, COVERAGE_BETA_TASK_TYPE, "last_30d"),
  );
  // A failed lookup isn't pending and has no data, so it counts as no work
  // there rather than blocking the page.
  const taskType =
    !oos.isPending && !beta.isPending
      ? pickDefaultTaskType(oos.data, beta.data)
      : null;
  return { taskType };
}

export function useViewableUsers() {
  return useQuery<InsightsUserBriefSchema[]>({
    queryKey: ["user-insights", "viewable-users"],
    queryFn: async () => {
      const res = await mvpApiListViewableUsers();
      if (res.error) throw new Error(`Failed to load users: ${res.error}`);
      return (res.data ?? []) as InsightsUserBriefSchema[];
    },
    staleTime: 10 * 60 * 1000,
  });
}
